import React, { useCallback, useEffect, useState } from "react";
import {
  Card,
  Typography,
  Flex,
  Button,
  Row,
  Col,
  Tag,
  Spin,
  Alert,
  Modal,
  Upload,
  Input,
  message,
  theme,
  LeftOutlined,
  UploadOutlined,
  DownloadOutlined,
} from "@/shared/antd-imports";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import clientPortalAPI from "@/services/api";
import { stripHtml } from "@/utils/escapeHtml";
import { InvoiceDetails } from "@/types";
import type { UploadFile } from "antd/es/upload/interface";

const { Title, Text } = Typography;
const { TextArea } = Input;

/** Invoices a client can send payment proof for: issued and not settled. */
const PAYMENT_PROOF_STATUSES = ["sent", "overdue"];

const InvoiceDetailsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { token } = theme.useToken();
  const [invoice, setInvoice] = useState<InvoiceDetails | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isPaymentModalVisible, setIsPaymentModalVisible] = useState(false);
  const [paymentNotes, setPaymentNotes] = useState("");
  const [paymentProofFile, setPaymentProofFile] = useState<UploadFile[]>([]);
  const [isSubmittingPayment, setIsSubmittingPayment] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const fetchInvoiceDetails = useCallback(async () => {
    if (!id) return;
    try {
      setIsLoading(true);
      setError(null);
      const response = await clientPortalAPI.getInvoiceDetails(id);

      if (response.done) {
        setInvoice(response.body as InvoiceDetails);
      } else {
        setError(t("invoices.details.loadError", { defaultValue: "Failed to load invoice details. Please try again later." }));
      }
    } catch (err) {
      setError(t("invoices.details.loadError", { defaultValue: "Failed to load invoice details. Please try again later." }));
      console.error("Invoice details API error:", err);
    } finally {
      setIsLoading(false);
    }
  }, [id, t]);

  useEffect(() => {
    fetchInvoiceDetails();
  }, [fetchInvoiceDetails]);

  const formatMoney = (amount: number, currency: string) => {
    try {
      return new Intl.NumberFormat(i18n.language || "en", { style: "currency", currency }).format(amount);
    } catch {
      return `${currency} ${amount.toFixed(2)}`;
    }
  };

  const formatDate = (value?: string | null) =>
    value ? new Date(value).toLocaleDateString(i18n.language || undefined) : t("invoices.details.notAvailable", { defaultValue: "N/A" });

  const handleDownloadInvoice = async () => {
    try {
      setIsDownloading(true);

      const clientToken = clientPortalAPI.getToken();
      const baseUrl = import.meta.env.VITE_API_BASE_URL || "http://localhost:3000/api/client-portal";

      const response = await fetch(`${baseUrl}/invoices/${id}/download?format=pdf`, {
        method: "GET",
        headers: {
          "x-client-token": clientToken || "",
        },
      });

      if (!response.ok) {
        message.error(t("invoices.details.downloadError", { defaultValue: "Failed to download invoice" }));
        return;
      }

      const contentType = response.headers.get("content-type");
      const contentDisposition = response.headers.get("content-disposition");
      let filename = `invoice-${invoice?.invoiceNumber || "invoice"}.pdf`;
      const filenameMatch = contentDisposition?.match(/filename="(.+)"/);
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1];
      }

      const arrayBuffer = await response.arrayBuffer();
      const blob = new Blob([arrayBuffer], { type: contentType || "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      message.success(t("invoices.details.downloadSuccess", { defaultValue: "Invoice downloaded" }));
    } catch (err) {
      console.error("Download error:", err);
      message.error(t("invoices.details.downloadError", { defaultValue: "Failed to download invoice" }));
    } finally {
      setIsDownloading(false);
    }
  };

  const closePaymentModal = () => {
    setIsPaymentModalVisible(false);
    setPaymentNotes("");
    setPaymentProofFile([]);
  };

  const handleSubmitPaymentProof = async () => {
    const notes = paymentNotes.trim();
    const file = paymentProofFile[0]?.originFileObj;

    if (!file && !notes) {
      message.warning(t("invoices.details.proofRequired", { defaultValue: "Attach a file or add a payment reference" }));
      return;
    }

    try {
      setIsSubmittingPayment(true);

      let proofUrl: string | undefined;
      if (file) {
        const uploadResponse = await clientPortalAPI.uploadFile(file, "payment_proof");
        if (!uploadResponse.done) {
          message.error(t("invoices.details.proofUploadError", { defaultValue: "Failed to upload the payment proof file" }));
          return;
        }
        proofUrl = uploadResponse.body.url;
      }

      const response = await clientPortalAPI.submitPaymentProof(id!, {
        proofUrl,
        notes: notes || undefined,
      });

      if (!response.done) {
        message.error(t("invoices.details.proofSubmitError", { defaultValue: "Failed to submit payment proof" }));
        return;
      }

      message.success(
        t("invoices.details.proofSubmitted", {
          defaultValue: "Payment proof submitted. Your provider will confirm the payment.",
        })
      );
      closePaymentModal();
      fetchInvoiceDetails();
    } catch (err) {
      console.error("Payment proof submission error:", err);
      message.error(t("invoices.details.proofSubmitError", { defaultValue: "Failed to submit payment proof" }));
    } finally {
      setIsSubmittingPayment(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case "paid":
        return "success";
      case "sent":
        return "processing";
      case "overdue":
        return "error";
      default:
        return "default";
    }
  };

  const getStatusText = (status: string) => {
    switch (status?.toLowerCase()) {
      case "paid":
        return t("invoices.paid", { defaultValue: "Paid" });
      case "sent":
        return t("invoices.sent", { defaultValue: "Sent" });
      case "overdue":
        return t("invoices.overdue", { defaultValue: "Overdue" });
      case "cancelled":
        return t("invoices.cancelled", { defaultValue: "Cancelled" });
      default:
        return status;
    }
  };

  const backButton = (
    <Button
      icon={<LeftOutlined />}
      onClick={() => navigate(-1)}
      type="text"
      style={{ boxShadow: "none" }}
      aria-label={t("invoices.details.back", { defaultValue: "Back" })}
    />
  );

  if (isLoading) {
    return (
      <div style={{ minHeight: "100vh", padding: 24, width: "100%" }}>
        <Flex justify="center" align="center" style={{ height: 400 }}>
          <Spin size="large" />
        </Flex>
      </div>
    );
  }

  if (error || !invoice) {
    return (
      <div style={{ minHeight: "100vh", padding: 24, width: "100%" }}>
        <Flex align="center" gap={12} style={{ marginBottom: 16 }}>
          {backButton}
          <Title level={4} style={{ margin: 0 }}>
            {t("invoices.details.title", { defaultValue: "Invoice Details" })}
          </Title>
        </Flex>
        <Card style={{ borderRadius: 8 }}>
          <Alert
            message={t("invoices.errorLoading", { defaultValue: "Error loading invoices" })}
            description={error || t("invoices.details.notFound", { defaultValue: "Invoice not found" })}
            type="error"
            showIcon
            action={
              <Button size="small" onClick={fetchInvoiceDetails}>
                {t("invoices.details.retry", { defaultValue: "Retry" })}
              </Button>
            }
          />
        </Card>
      </div>
    );
  }

  const notAvailable = t("invoices.details.notAvailable", { defaultValue: "N/A" });
  const invoiceNumber = invoice.invoiceNumber || notAvailable;
  const invoiceTotal = invoice.amount || 0;
  const currency = invoice.currency || "USD";
  const canSubmitProof = PAYMENT_PROOF_STATUSES.includes(invoice.status?.toLowerCase());

  return (
    <div style={{ minHeight: "100vh", padding: 24, width: "100%" }}>
      <div style={{ width: "100%" }}>
        <Flex align="center" gap={12} wrap="wrap" style={{ marginBottom: 16 }}>
          {backButton}
          <Title level={4} style={{ margin: 0 }}>
            {invoiceNumber}
          </Title>
          <Tag color={getStatusColor(invoice.status)}>{getStatusText(invoice.status)}</Tag>
        </Flex>

        {invoice.paymentProofSubmittedAt && invoice.status?.toLowerCase() !== "paid" && (
          <Alert
            style={{ marginBottom: 16 }}
            type="info"
            showIcon
            message={t("invoices.details.proofPendingTitle", { defaultValue: "Payment proof submitted" })}
            description={t("invoices.details.proofPendingDescription", {
              defaultValue: "Submitted on {{date}}. Waiting for your provider to confirm the payment.",
              date: formatDate(invoice.paymentProofSubmittedAt),
            })}
          />
        )}

        <Card style={{ borderRadius: 8, marginBottom: 24, width: "100%" }}>
          <Row gutter={[32, 24]} style={{ marginBottom: 24 }}>
            <Col xs={24} md={12}>
              <Text type="secondary">{t("invoices.details.billedTo", { defaultValue: "Billed to" })}</Text>
              <div style={{ marginTop: 4 }}>
                <Text strong>{invoice.client?.name || t("invoices.details.clientFallback", { defaultValue: "Client" })}</Text>
                <br />
                <Text>{invoice.client?.companyName}</Text>
                <br />
                <Text>{invoice.client?.email}</Text>
              </div>
            </Col>
            <Col xs={24} md={12}>
              <Row gutter={[16, 16]}>
                <Col span={12}>
                  <Text type="secondary">{t("invoices.details.invoiceNumber", { defaultValue: "Invoice #" })}</Text>
                  <br />
                  <Text strong>{invoiceNumber}</Text>
                </Col>
                <Col span={12} style={{ textAlign: "right" }}>
                  <Text type="secondary">{t("invoices.details.invoiceAmount", { defaultValue: "Invoice Amount" })}</Text>
                  <br />
                  <Title level={3} style={{ color: token.colorSuccess, margin: 0 }}>
                    {formatMoney(invoiceTotal, currency)}
                  </Title>
                </Col>
                <Col span={12}>
                  <Text type="secondary">{t("invoices.details.issueDate", { defaultValue: "Issue Date" })}</Text>
                  <br />
                  <Text strong>{formatDate(invoice.createdAt)}</Text>
                </Col>
                <Col span={12} style={{ textAlign: "right" }}>
                  <Text type="secondary">{t("invoices.dueDate", { defaultValue: "Due Date" })}</Text>
                  <br />
                  <Text strong>{formatDate(invoice.dueDate)}</Text>
                </Col>
              </Row>
            </Col>
          </Row>

          {invoice.request && (
            <Row gutter={32} style={{ marginBottom: 24 }}>
              <Col span={24}>
                <Text type="secondary">{t("invoices.service", { defaultValue: "Service" })}</Text>
                <br />
                <Text strong>{invoice.request.service?.name || notAvailable}</Text>
                <br />
                <Text>{stripHtml(invoice.request.service?.description || "")}</Text>
              </Col>
            </Row>
          )}

          <Flex gap={12} wrap="wrap">
            {canSubmitProof && (
              <Button type="primary" icon={<UploadOutlined />} onClick={() => setIsPaymentModalVisible(true)}>
                {t("invoices.details.submitPaymentProof", { defaultValue: "Submit payment proof" })}
              </Button>
            )}
            <Button icon={<DownloadOutlined />} onClick={handleDownloadInvoice} loading={isDownloading}>
              {t("invoices.details.downloadInvoice", { defaultValue: "Download Invoice" })}
            </Button>
          </Flex>
        </Card>
      </div>

      <Modal
        title={t("invoices.details.submitPaymentProof", { defaultValue: "Submit payment proof" })}
        open={isPaymentModalVisible}
        onCancel={closePaymentModal}
        onOk={handleSubmitPaymentProof}
        okText={t("invoices.details.submit", { defaultValue: "Submit" })}
        cancelText={t("invoices.details.cancel", { defaultValue: "Cancel" })}
        confirmLoading={isSubmittingPayment}
        width={600}
      >
        <Flex vertical gap={16} style={{ marginTop: 16 }}>
          <Alert
            message={t("invoices.details.proofInfoTitle", { defaultValue: "Payment confirmation" })}
            description={t("invoices.details.proofInfoDescription", {
              defaultValue:
                "Upload proof of payment and add a reference. Your provider will review it and mark the invoice as paid once the payment is confirmed.",
            })}
            type="info"
            showIcon
          />

          <Flex vertical gap={8}>
            <Text strong>{t("invoices.details.paymentAmount", { defaultValue: "Payment amount" })}</Text>
            <Text style={{ fontSize: 18 }}>{formatMoney(invoiceTotal, currency)}</Text>
          </Flex>

          <Flex vertical gap={8}>
            <Text strong>{t("invoices.details.uploadProof", { defaultValue: "Upload payment proof" })}</Text>
            <Upload
              maxCount={1}
              fileList={paymentProofFile}
              onChange={({ fileList }) => setPaymentProofFile(fileList)}
              beforeUpload={() => false}
              accept="image/*,.pdf"
            >
              <Button icon={<UploadOutlined />}>{t("invoices.details.selectFile", { defaultValue: "Select file" })}</Button>
            </Upload>
            <Text type="secondary">
              {t("invoices.details.supportedFormats", { defaultValue: "Supported formats: images, PDF" })}
            </Text>
          </Flex>

          <Flex vertical gap={8}>
            <Text strong>{t("invoices.details.notesLabel", { defaultValue: "Reference or notes (optional)" })}</Text>
            <TextArea
              rows={4}
              maxLength={2000}
              placeholder={t("invoices.details.notesPlaceholder", {
                defaultValue: "Add a transaction reference, payment method or any other notes...",
              })}
              value={paymentNotes}
              onChange={e => setPaymentNotes(e.target.value)}
            />
          </Flex>
        </Flex>
      </Modal>
    </div>
  );
};

export default InvoiceDetailsPage;
